import { useState, useEffect } from 'react';
import { 
  NicheKey, 
  Client, 
  Supplier, 
  Appointment, 
  StorefrontProfile,
  InventoryBatch,
  StockMovement,
  ClinicalPatient,
  ClinicalConsultation,
  ClinicalVaccination,
  ClinicalExamVault,
  ConsumedMaterialItem,
  AccountantAccess
} from '../types';
import { ProfileFinancialState } from '../components/ProfileEditorModal';
import { PipelineItem } from '../components/WorkPipelineKanban';
import { UserPlanTier } from '../components/PlanUpgradeModal';
import { 
  loadErpClients, 
  saveErpClients,
  loadErpSuppliers,
  saveErpSuppliers,
  loadErpAppointments,
  saveErpAppointments,
  loadErpKanban,
  saveErpKanban,
  loadErpFinancialState,
  saveErpFinancialState,
  loadErpProfile,
  saveErpProfile,
  loadErpPlanTier,
  saveErpPlanTier,
  loadErpInventoryBatches,
  saveErpInventoryBatches,
  loadErpStockMovements,
  saveErpStockMovements,
  loadErpClinicalPatients,
  saveErpClinicalPatients,
  loadErpClinicalConsultations,
  saveErpClinicalConsultations,
  loadErpClinicalVaccinations,
  saveErpClinicalVaccinations,
  loadErpClinicalExams,
  saveErpClinicalExams
} from '../store/erpStorage';
import { useAuth } from '../../../contexts/AuthContext';
import { useTeamPermissions } from '../../../contexts/TeamPermissionsContext';
import { supabase } from '../../../lib/supabase';
import { generateCfmvSignatureHash, generateReceiptNumber } from '../utils/cryptoUtils';
const isValidUuid = (str?: string): boolean => {
  return Boolean(str && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str));
};

export type ActiveView = 
  | 'hub' 
  | 'agendar'
  | 'orcamento' 
  | 'custos' 
  | 'recibo' 
  | 'os' 
  | 'gestao' 
  | 'agenda' 
  | 'clientes' 
  | 'fornecedores' 
  | 'cadastro' 
  | 'assinatura'
  | 'radar'
  | 'cockpit'
  | 'equipe'
  | 'estoque_hub'
  | 'prontuario_soap'
  | 'retorno_vacinas'
  | 'cofre_exames'
  | 'portal_contador'
  | 'tour';

const DEFAULT_FINANCIAL_STATE: ProfileFinancialState = {
  desiredProLabore: 4500,
  workHoursPerDay: 8,
  workDaysPerWeek: 5,
  vacationWeeksPerYear: 4,
  fixedCosts: [
    { id: '1', name: 'Aluguel do Espaço / Oficina', monthlyValue: 900 },
    { id: '2', name: 'Energia Elétrica & Água', monthlyValue: 280 },
    { id: '3', name: 'Internet & Celular Comercial', monthlyValue: 160 },
    { id: '4', name: 'MEI / DAS e Contador', monthlyValue: 120 },
    { id: '5', name: 'Ferramentas, Softwares e Manutenção', monthlyValue: 150 }
  ],
  minMarginPercent: 25
};

const DEFAULT_CLIENTS: Client[] = [
  {
    id: 'c1',
    userId: 'demo_user',
    name: 'Beatriz Lima',
    phone: '(11) 98765-4321',
    email: 'beatriz.lima@email.com',
    document: '123.456.789-00',
    address: 'Av. Paulista, 1578 - Bela Vista, São Paulo - SP',
    tags: ['VIP', 'Recorrente'],
    totalSpent: 480,
    ordersCount: 2,
    source: 'chatbot_vitrine',
    notes: 'Prefere atendimento nas manhãs de quinta-feira.',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'c2',
    userId: 'demo_user',
    name: 'Roberto Dias',
    phone: '(11) 91234-5678',
    email: 'roberto.dias@email.com',
    document: '234.567.890-11',
    address: 'Rua Augusta, 900 - Consolação, São Paulo - SP',
    tags: ['Reforma', 'Obra'],
    totalSpent: 1550,
    ordersCount: 2,
    source: 'orcamento',
    vehicleData: {
      plate: 'BRA2E19',
      brand: 'Volkswagen',
      model: 'Gol 1.6 MSI Flex',
      modelYear: 2022,
      color: 'Prata',
      engine: '1.6 8V TotalFlex',
      currentMileageKm: 45200
    },
    notes: 'Solicitou laudo NBR 5410 com ART/TRT.',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'c3',
    userId: 'demo_user',
    name: 'Condomínio Edifício Solar',
    phone: '(11) 97777-8888',
    email: 'sindico@condominiosolar.com.br',
    document: '12.345.678/0001-90',
    address: 'Alameda Santos, 1200 - Cerqueira César, São Paulo - SP',
    tags: ['Empresarial', 'Contrato'],
    totalSpent: 2900,
    ordersCount: 3,
    source: 'cnpj_lookup',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

const DEFAULT_SUPPLIERS: Supplier[] = [
  {
    id: 'sup_1',
    userId: 'demo_user',
    name: 'Distribuidora & Atacadista de Materiais Elétricos Paulista LTDA',
    tradeName: 'Mega Paulista Elétrica',
    cnpj: '33.123.456/0001-89',
    category: 'Material Elétrico & Iluminação',
    phone: '(11) 98888-7777',
    email: 'vendas@megapaulista.com.br',
    address: 'Rua Santa Ifigênia, 450 - Centro, São Paulo - SP',
    paymentTerms: 'Boleto 28 ddl faturado',
    rating: 5,
    source: 'cnpj_lookup',
    suppliedProductsCount: 18,
    notes: 'Fornecedor oficial de condutores e barramentos.',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'sup_2',
    userId: 'demo_user',
    name: 'Auto Peças & Distribuidora Brasil Central LTDA',
    tradeName: 'Bandeirantes Auto Peças',
    cnpj: '12.345.678/0001-90',
    category: 'Autopeças & Mecânica',
    phone: '(11) 97777-6666',
    email: 'pedidos@bandeirantespecas.com.br',
    address: 'Av. das Nações Unidas, 12000 - Santo Amaro, São Paulo - SP',
    paymentTerms: 'PIX à vista com 5% de desconto',
    rating: 5,
    source: 'nfe_upload',
    suppliedProductsCount: 42,
    notes: 'Entrega rápida via motoboy em 2h para oficinas.',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

const DEFAULT_APPOINTMENTS: Appointment[] = [
  {
    id: 'appt_1',
    userId: 'demo_user',
    clientName: 'Beatriz Lima',
    clientPhone: '(11) 98765-4321',
    clientAddress: 'Av. Paulista, 1578 - Bela Vista, São Paulo - SP',
    serviceName: 'Instalação de Perfil de LED',
    durationMinutes: 120,
    scheduledDate: new Date().toISOString().slice(0, 10),
    scheduledTime: '10:00',
    price: 250,
    status: 'confirmed',
    notes: 'Sala e corredor',
    historyTimeline: [
      {
        id: 'h1',
        timestamp: new Date(Date.now() - 3600000 * 4).toISOString(),
        type: 'bot_booking',
        text: 'Cliente realizou agendamento online pelo Chatbot da Vitrine (rmax.link/fabio-eletricista).'
      }
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

const DEFAULT_KANBAN_ITEMS: PipelineItem[] = [
  { id: '1', title: 'Orçamento de Iluminação LED', clientName: 'Marcos Silveira', finalPrice: 850, nicheKey: 'eletricista', stage: 'draft', updatedAt: 'Hoje' },
  { id: '2', title: 'Troca de QDT & Disjuntores', clientName: 'Condomínio Solar', finalPrice: 1450, nicheKey: 'eletricista', stage: 'approved', updatedAt: '18/08' },
  { id: '3', title: 'Instalação Split 18k BTUs', clientName: 'Dra. Fernanda', finalPrice: 480, nicheKey: 'hvac', stage: 'in_progress', updatedAt: '17/08' },
  { id: '4', title: 'Reforma Hidráulica Banheiro', clientName: 'Roberto Dias', finalPrice: 1200, nicheKey: 'encanador', stage: 'paid', updatedAt: '15/08' }
];

const DEFAULT_PROFILE: StorefrontProfile = {
  id: 'store_1',
  userId: 'demo_user',
  slug: 'fabio-eletricista',
  displayName: 'Fábio Prestador de Serviços',
  tagline: 'Construção, Elétrica, Hidráulica e Climatização',
  bio: 'Profissional Eletricista com mais de 11 anos de experiência atendendo residências e condomínios com rapidez, limpeza e segurança.',
  yearsExperience: '11 anos e 10 meses',
  nicheBadges: ['Construção', 'Elétrica Residencial', 'Hidráulica & Encanamento', 'Impermeabilização'],
  whatsappNumber: '11999999999',
  instagramHandle: '@fabioprestador',
  facebookHandle: 'fb.com/fabio.prestador',
  tiktokHandle: '@fabioprestador.oficial',
  email: 'contato@fabioprestador.com.br',
  addressDisplay: 'São Paulo - SP',
  accentColor: '#10b981',
  isPublished: true,
  viewCount: 2340,
  portfolioItems: []
};

export function useErpPlaygroundState() {
  const [currentView, setCurrentView] = useState<ActiveView>(() => {
    if (typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const v = urlParams.get('view') as ActiveView;
        if (v) return v;
      } catch {}
    }
    return 'hub';
  });

  const [selectedNiche, setSelectedNiche] = useState<NicheKey>(() => {
    if (typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const n = urlParams.get('niche') as NicheKey;
        if (n) return n;
      } catch {}
    }
    return 'eletricista';
  });

  const { session, isMasterUser } = useAuth();
  const { effectiveWorkspaceId } = useTeamPermissions();
  const userId = session?.user?.id;
  const activeWorkspaceId = effectiveWorkspaceId || userId;
  const currentUserId = userId || 'demo_user';

  // Sincronização inicial com o Supabase quando usuário está autenticado
  useEffect(() => {
    if (!activeWorkspaceId) return;

    let isMounted = true;

    async function syncFromSupabase() {
      try {
        // 1. Clientes reais do workspace
        const { data: dbClients } = await supabase
          .from('clients')
          .select('*')
          .eq('user_id', activeWorkspaceId!);
        if (isMounted && dbClients) {
          const mappedClients: Client[] = dbClients.map((c: any) => ({
            id: c.id,
            userId: c.user_id,
            name: c.name,
            phone: c.phone || '',
            email: c.email || '',
            document: c.document || '',
            address: c.address || '',
            notes: c.notes || '',
            tags: Array.isArray(c.tags) ? c.tags : [],
            totalSpent: Number(c.total_spent) || 0,
            ordersCount: Number(c.orders_count) || 0,
            source: c.source || 'sistema',
            createdAt: c.created_at || new Date().toISOString(),
            updatedAt: c.updated_at || new Date().toISOString()
          }));
          setClientsInternal(mappedClients);
          saveErpClients(mappedClients);
        }

        // 2. Fornecedores reais do workspace
        const { data: dbSuppliers } = await supabase
          .from('suppliers')
          .select('*')
          .eq('user_id', activeWorkspaceId!);
        if (isMounted && dbSuppliers) {
          const mappedSuppliers: Supplier[] = dbSuppliers.map((s: any) => ({
            id: s.id,
            userId: s.user_id,
            name: s.name,
            tradeName: s.trade_name || '',
            cnpj: s.cnpj || '',
            category: s.category || 'Geral',
            phone: s.phone || '',
            email: s.email || '',
            address: s.address || '',
            notes: s.notes || '',
            rating: Number(s.rating) || 5,
            createdAt: s.created_at || new Date().toISOString(),
            updatedAt: s.updated_at || new Date().toISOString()
          }));
          setSuppliersInternal(mappedSuppliers);
          saveErpSuppliers(mappedSuppliers);
        }

        // 3. Agendamentos reais do workspace
        const { data: dbAppointments } = await supabase
          .from('appointments')
          .select('*')
          .eq('user_id', activeWorkspaceId!);
        if (isMounted && dbAppointments) {
          const mappedAppts: Appointment[] = dbAppointments.map((a: any) => ({
            id: a.id,
            userId: a.user_id,
            clientName: a.client_name,
            clientPhone: a.client_phone || '',
            serviceName: a.service_name || 'Serviço',
            durationMinutes: Number(a.duration_minutes) || 60,
            scheduledDate: a.scheduled_date || new Date().toISOString().slice(0, 10),
            scheduledTime: a.scheduled_time || '10:00',
            price: Number(a.price) || 0,
            status: a.status || 'confirmed',
            notes: a.notes || '',
            createdAt: a.created_at || new Date().toISOString(),
            updatedAt: a.updated_at || new Date().toISOString()
          }));
          setAppointmentsInternal(mappedAppts);
          saveErpAppointments(mappedAppts);
        }

        // 4. Orçamentos para o Kanban comercial do workspace
        const { data: dbQuotes } = await supabase
          .from('quotes')
          .select('id, service_name, client_name, final_price, status, updated_at')
          .eq('user_id', activeWorkspaceId!);
        if (isMounted && dbQuotes) {
          const kanbanMapped: PipelineItem[] = dbQuotes.map((q: any) => ({
            id: q.id,
            title: q.service_name || 'Orçamento sem título',
            clientName: q.client_name || 'Cliente',
            finalPrice: Number(q.final_price) || 0,
            stage: (q.status || 'draft') as PipelineItem['stage'],
            updatedAt: q.updated_at ? new Date(q.updated_at).toLocaleDateString('pt-BR') : 'Hoje'
          }));
          setKanbanItemsInternal(kanbanMapped);
          saveErpKanban(kanbanMapped);
        }

        // 5. Lotes de Estoque Enterprise
        const { data: dbBatches } = await supabase
          .from('inventory_batches')
          .select('*, materials(name, price)')
          .order('expiration_date', { ascending: true });
        if (isMounted && dbBatches) {
          const mappedBatches: InventoryBatch[] = dbBatches.map((b: any) => ({
            id: b.id,
            userId: b.user_id,
            materialId: b.material_id,
            materialName: b.materials?.name || 'Insumo',
            batchCode: b.batch_code,
            expirationDate: b.expiration_date,
            costPrice: Number(b.cost_price || 0),
            salePrice: Number(b.materials?.price) || (Number(b.cost_price || 0) * 1.6),
            initialQuantity: Number(b.initial_quantity || 0),
            currentQuantity: Number(b.current_quantity || 0),
            createdAt: b.created_at,
            updatedAt: b.updated_at
          }));
          setInventoryBatchesInternal(mappedBatches);
          saveErpInventoryBatches(mappedBatches);
        }

        // 6. Movimentações de Estoque (Ledger)
        const { data: dbMovements } = await supabase
          .from('stock_movements')
          .select('*, materials(name), inventory_batches(batch_code)')
          .order('created_at', { ascending: false });
        if (isMounted && dbMovements) {
          const mappedMovements: StockMovement[] = dbMovements.map((m: any) => ({
            id: m.id,
            userId: m.user_id,
            materialId: m.material_id,
            materialName: m.materials?.name || 'Insumo',
            batchId: m.batch_id || undefined,
            batchCode: m.inventory_batches?.batch_code || undefined,
            type: m.type,
            quantity: Number(m.quantity || 0),
            unitCost: Number(m.unit_cost || 0),
            totalCost: Number(m.total_cost) || (Number(m.quantity || 0) * Number(m.unit_cost || 0)),
            balanceAfter: Number(m.balance_after || 0),
            referenceId: m.reference_id || undefined,
            reasonNotes: m.reason_notes || undefined,
            createdAt: m.created_at
          }));
          setStockMovementsInternal(mappedMovements);
          saveErpStockMovements(mappedMovements);
        }

        // 7. Pacientes / Pets Clínicos
        const { data: dbPatients } = await supabase
          .from('clinical_patients')
          .select('*, clients(name, phone)')
          .order('created_at', { ascending: false });
        if (isMounted && dbPatients) {
          const mappedPatients: ClinicalPatient[] = dbPatients.map((p: any) => ({
            id: p.id,
            userId: p.user_id,
            clientId: p.client_id,
            clientName: p.clients?.name || 'Tutor',
            clientPhone: p.clients?.phone || '',
            kind: p.kind || 'pet',
            name: p.name,
            species: p.species || 'Canina',
            breed: p.breed || 'SRD',
            gender: p.gender || 'M',
            isNeutered: Boolean(p.is_neutered),
            microchipNumber: p.microchip_number || undefined,
            coatColor: p.coat_color || undefined,
            birthDate: p.birth_date || undefined,
            bloodType: p.blood_type || undefined,
            knownAllergies: p.known_allergies || undefined,
            chronicConditions: p.chronic_conditions || undefined,
            emergencyContact: p.emergency_contact || undefined,
            active: p.active !== false,
            createdAt: p.created_at,
            updatedAt: p.updated_at
          }));
          setClinicalPatientsInternal(mappedPatients);
          saveErpClinicalPatients(mappedPatients);
        }

        // 8. Prontuários SOAP
        const { data: dbConsultations } = await supabase
          .from('clinical_consultations')
          .select('*, clinical_patients(name)')
          .order('created_at', { ascending: false });
        if (isMounted && dbConsultations) {
          const mappedConsultations: ClinicalConsultation[] = dbConsultations.map((c: any) => ({
            id: c.id,
            userId: c.user_id,
            patientId: c.patient_id,
            patientName: c.clinical_patients?.name || 'Paciente',
            appointmentId: c.appointment_id || undefined,
            staffMemberId: c.staff_member_id || undefined,
            subjectiveAnamnesis: c.subjective_anamnesis || '',
            objectiveExam: c.objective_exam || {},
            assessmentDiagnosis: c.assessment_diagnosis || '',
            planTreatment: c.plan_treatment || '',
            prescriptions: Array.isArray(c.prescriptions) ? c.prescriptions : [],
            consumedMaterials: Array.isArray(c.consumed_materials) ? c.consumed_materials : [],
            consultationFee: Number(c.consultation_fee || 0),
            materialsFee: Number(c.materials_fee || 0),
            totalAmount: Number(c.total_amount) || (Number(c.consultation_fee || 0) + Number(c.materials_fee || 0)),
            status: c.status || 'in_progress',
            finalizedAt: c.finalized_at || undefined,
            professionalSignatureHash: c.professional_signature_hash || undefined,
            createdAt: c.created_at,
            updatedAt: c.updated_at
          }));
          setClinicalConsultationsInternal(mappedConsultations);
          saveErpClinicalConsultations(mappedConsultations);
        }

        // 9. Vacinas & Retornos
        const { data: dbVaccines } = await supabase
          .from('clinical_vaccinations')
          .select('*, clinical_patients(name, clients(name, phone))')
          .order('next_due_date', { ascending: true });
        if (isMounted && dbVaccines) {
          const mappedVaccines: ClinicalVaccination[] = dbVaccines.map((v: any) => ({
            id: v.id,
            userId: v.user_id,
            patientId: v.patient_id,
            patientName: v.clinical_patients?.name || 'Paciente',
            tutorName: v.clinical_patients?.clients?.name || 'Tutor',
            tutorPhone: v.clinical_patients?.clients?.phone || '',
            consultationId: v.consultation_id || undefined,
            materialId: v.material_id || undefined,
            batchId: v.batch_id || undefined,
            vaccineName: v.vaccine_name,
            appliedAt: v.applied_at,
            nextDueDate: v.next_due_date,
            reminderCount: Number(v.reminder_count || 0),
            lastRemindedAt: v.last_reminded_at || undefined,
            status: v.status || 'pending',
            createdAt: v.created_at
          }));
          setClinicalVaccinationsInternal(mappedVaccines);
          saveErpClinicalVaccinations(mappedVaccines);
        }

        // 10. Cofre de Exames DICOM
        const { data: dbExams } = await supabase
          .from('clinical_exams_vault')
          .select('*, clinical_patients(name)')
          .order('created_at', { ascending: false });
        if (isMounted && dbExams) {
          const mappedExams: ClinicalExamVault[] = dbExams.map((e: any) => ({
            id: e.id,
            userId: e.user_id,
            patientId: e.patient_id,
            patientName: e.clinical_patients?.name || 'Paciente',
            consultationId: e.consultation_id || undefined,
            title: e.title,
            examType: e.exam_type,
            reportPdfUrl: e.report_pdf_url || undefined,
            previewImageUrl: e.preview_image_url || undefined,
            rawDicomR2Path: e.raw_dicom_r2_path || undefined,
            rawFileSizeBytes: Number(e.raw_file_size_bytes || 0),
            dicomMetadata: e.dicom_metadata || undefined,
            legalRetentionUntil: e.legal_retention_until || '',
            createdAt: e.created_at
          }));
          setClinicalExamsInternal(mappedExams);
          saveErpClinicalExams(mappedExams);
        }

        // 11. Acessos do Contador
        const { data: dbAccountants } = await supabase
          .from('accountant_access')
          .select('*')
          .order('created_at', { ascending: false });
        if (isMounted && dbAccountants) {
          const mappedAcc: AccountantAccess[] = dbAccountants.map((a: any) => ({
            id: a.id,
            workspaceOwnerId: a.workspace_owner_id,
            accountantUserId: a.accountant_user_id || undefined,
            accountantEmail: a.accountant_email,
            accountantName: a.accountant_name || undefined,
            accountingFirm: a.accounting_firm || undefined,
            crcNumber: a.crc_number || undefined,
            status: a.status || 'pending',
            invitedAt: a.invited_at,
            acceptedAt: a.accepted_at || undefined,
            permissions: a.permissions || {
              canExportTax: true,
              canViewReceipts: true,
              canViewMaterials: true,
              canViewClinicalData: false
            },
            createdAt: a.created_at
          }));
          setAccountantAccesses(mappedAcc);
        }
      } catch (err) {
        console.warn('Supabase initial fetch fallback:', err);
      }
    }

    syncFromSupabase();

    return () => {
      isMounted = false;
    };
  }, [userId, activeWorkspaceId]);

  // Planos & Upgrade Modal - Suporta simulação direta via URL (?plan=free | pro | especialista | empresa)
  const [simulatedPlanTier, setSimulatedPlanTierState] = useState<UserPlanTier>(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const planParam = urlParams.get('plan')?.toLowerCase();
      if (planParam === 'free' || planParam === 'gratis' || planParam === 'grátis') return 'free';
      if (planParam === 'pro') return 'pro';
      if (planParam === 'especialista' || planParam === 'specialist' || planParam === 'premium') return 'premium';
      if (planParam === 'empresa' || planParam === 'enterprise' || planParam === 'empresarial') return 'enterprise';
    } catch (_) {}
    const saved = loadErpPlanTier('enterprise');
    return saved;
  });

  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const planParam = urlParams.get('plan')?.toLowerCase();
      if (planParam === 'free' || planParam === 'gratis' || planParam === 'grátis') setSimulatedPlanTierState('free');
      else if (planParam === 'pro') setSimulatedPlanTierState('pro');
      else if (planParam === 'especialista' || planParam === 'specialist' || planParam === 'premium') setSimulatedPlanTierState('premium');
      else if (planParam === 'empresa' || planParam === 'enterprise' || planParam === 'empresarial') setSimulatedPlanTierState('enterprise');
      else if (isMasterUser) {
        setSimulatedPlanTierState('enterprise');
      }
    } catch (_) {
      if (isMasterUser) setSimulatedPlanTierState('enterprise');
    }
  }, [isMasterUser]);

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeRequiredTier, setUpgradeRequiredTier] = useState<'pro' | 'premium' | 'enterprise'>('enterprise');
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('');
  const [upgradeFeatureDescription, setUpgradeFeatureDescription] = useState('');

  const setSimulatedPlanTier = (tier: UserPlanTier) => {
    setSimulatedPlanTierState(tier);
    saveErpPlanTier(tier);
  };

  const canAccess = (requiredTier: 'free' | 'pro' | 'premium' | 'enterprise') => {
    if (isMasterUser || simulatedPlanTier === 'enterprise') return true;
    if (simulatedPlanTier === 'premium') return requiredTier === 'free' || requiredTier === 'pro' || requiredTier === 'premium';
    if (simulatedPlanTier === 'pro') return requiredTier === 'free' || requiredTier === 'pro';
    return requiredTier === 'free';
  };

  const handleActionClick = (
    requiredTier: 'free' | 'pro' | 'premium' | 'enterprise',
    action: () => void,
    featureName: string,
    featureDesc?: string
  ) => {
    if (canAccess(requiredTier)) {
      action();
    } else {
      setUpgradeRequiredTier(requiredTier === 'enterprise' ? 'enterprise' : (requiredTier === 'pro' ? 'pro' : 'premium'));
      setUpgradeFeatureName(featureName);
      setUpgradeFeatureDescription(featureDesc || '');
      setIsUpgradeModalOpen(true);
    }
  };

  // Estado Financeiro & VHB
  const [financialState, setFinancialStateInternal] = useState<ProfileFinancialState>(() => loadErpFinancialState(DEFAULT_FINANCIAL_STATE));

  const setFinancialState = async (state: ProfileFinancialState) => {
    setFinancialStateInternal(state);
    saveErpFinancialState(state);

    if (userId) {
      try {
        await supabase.from('financial_configs').upsert({
          user_id: userId,
          desired_pro_labore: state.desiredProLabore,
          work_hours_per_day: state.workHoursPerDay,
          work_days_per_week: state.workDaysPerWeek,
          vacation_weeks_per_year: state.vacationWeeksPerYear,
          fixed_costs: state.fixedCosts,
          min_margin_percent: state.minMarginPercent,
          updated_at: new Date().toISOString()
        });
      } catch (err) {
        console.warn('Erro ao salvar financial_configs no Supabase:', err);
      }
    }
  };

  const totalFixed = (financialState.fixedCosts || []).reduce((acc, c) => acc + (c.monthlyValue || 0), 0);
  const workWeeksPerYear = Math.max(1, 52 - (financialState.vacationWeeksPerYear || 4));
  const monthlyProductiveHours = Math.max(1, (financialState.workHoursPerDay || 8) * (financialState.workDaysPerWeek || 5) * (workWeeksPerYear / 12));
  const calculatedVhbRate = ((financialState.desiredProLabore || 4500) + totalFixed) / monthlyProductiveHours;

  // Clientes
  const [clients, setClientsInternal] = useState<Client[]>(() => loadErpClients(DEFAULT_CLIENTS));

  const saveClient = async (client: Client) => {
    const isNew = !client.id || client.id.startsWith('c');
    const clientId = !isNew ? client.id : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `cli_${Date.now()}`);
    const clientToSave: Client = {
      ...client,
      id: clientId,
      userId: currentUserId,
      updatedAt: new Date().toISOString()
    };

    setClientsInternal(prev => {
      const exists = prev.some(c => c.id === clientToSave.id);
      const updated = exists ? prev.map(c => c.id === clientToSave.id ? clientToSave : c) : [clientToSave, ...prev];
      saveErpClients(updated);
      return updated;
    });

    if (userId) {
      try {
        await supabase.from('clients').upsert({
          id: clientToSave.id,
          user_id: userId,
          name: clientToSave.name,
          phone: clientToSave.phone,
          email: clientToSave.email,
          document: clientToSave.document,
          address: clientToSave.address,
          notes: clientToSave.notes,
          tags: clientToSave.tags,
          total_spent: clientToSave.totalSpent,
          orders_count: clientToSave.ordersCount,
          updated_at: clientToSave.updatedAt
        });
      } catch (err) {
        console.warn('Erro ao salvar cliente no Supabase:', err);
      }
    }
  };

  const updateClientVehicle = (clientName: string, vehicleData: any) => {
    setClientsInternal(prev => {
      const updated = prev.map(c => {
        if (c.name.toLowerCase() === clientName.toLowerCase()) {
          return {
            ...c,
            vehicleData,
            vehicleHistory: c.vehicleHistory ? [...c.vehicleHistory, vehicleData] : [vehicleData]
          };
        }
        return c;
      });
      saveErpClients(updated);
      return updated;
    });
  };

  // Fornecedores
  const [suppliers, setSuppliersInternal] = useState<Supplier[]>(() => loadErpSuppliers(DEFAULT_SUPPLIERS));

  const saveSupplier = async (supplier: Supplier) => {
    const isNew = !supplier.id || supplier.id.startsWith('sup_');
    const supplierId = !isNew ? supplier.id : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `sup_${Date.now()}`);
    const supplierToSave: Supplier = {
      ...supplier,
      id: supplierId,
      userId: currentUserId,
      updatedAt: new Date().toISOString()
    };

    setSuppliersInternal(prev => {
      const exists = prev.some(s => s.id === supplierToSave.id || (supplierToSave.cnpj && s.cnpj === supplierToSave.cnpj));
      const updated = exists ? prev.map(s => (s.id === supplierToSave.id || (supplierToSave.cnpj && s.cnpj === supplierToSave.cnpj)) ? supplierToSave : s) : [supplierToSave, ...prev];
      saveErpSuppliers(updated);
      return updated;
    });

    if (userId) {
      try {
        await supabase.from('suppliers').upsert({
          id: supplierToSave.id,
          user_id: userId,
          name: supplierToSave.name,
          trade_name: supplierToSave.tradeName,
          cnpj: supplierToSave.cnpj,
          category: supplierToSave.category,
          phone: supplierToSave.phone,
          email: supplierToSave.email,
          address: supplierToSave.address,
          notes: supplierToSave.notes,
          rating: supplierToSave.rating,
          updated_at: supplierToSave.updatedAt
        });
      } catch (err) {
        console.warn('Erro ao salvar fornecedor no Supabase:', err);
      }
    }
  };

  const deleteSupplier = async (id: string) => {
    setSuppliersInternal(prev => {
      const updated = prev.filter(s => s.id !== id);
      saveErpSuppliers(updated);
      return updated;
    });

    if (userId) {
      try {
        await supabase.from('suppliers').delete().eq('id', id).eq('user_id', userId);
      } catch (err) {
        console.warn('Erro ao excluir fornecedor no Supabase:', err);
      }
    }
  };

  // Agendamentos
  const [appointments, setAppointmentsInternal] = useState<Appointment[]>(() => loadErpAppointments(DEFAULT_APPOINTMENTS));

  const saveAppointment = async (appointment: Appointment) => {
    const isNew = !appointment.id || appointment.id.startsWith('appt_');
    const apptId = !isNew ? appointment.id : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `appt_${Date.now()}`);
    const apptToSave: Appointment = {
      ...appointment,
      id: apptId,
      userId: currentUserId,
      updatedAt: new Date().toISOString()
    };

    setAppointmentsInternal(prev => {
      const updated = [...prev, apptToSave];
      saveErpAppointments(updated);
      return updated;
    });

    if (userId) {
      try {
        await supabase.from('appointments').upsert({
          id: apptToSave.id,
          user_id: userId,
          client_name: apptToSave.clientName,
          client_phone: apptToSave.clientPhone,
          service_name: apptToSave.serviceName,
          scheduled_date: apptToSave.scheduledDate,
          scheduled_time: apptToSave.scheduledTime,
          price: apptToSave.price,
          status: apptToSave.status,
          notes: apptToSave.notes,
          updated_at: apptToSave.updatedAt
        });
      } catch (err) {
        console.warn('Erro ao salvar agendamento no Supabase:', err);
      }
    }
  };

  const updateAppointmentStatus = async (id: string, status: Appointment['status']) => {
    setAppointmentsInternal(prev => {
      const updated = prev.map(a => a.id === id ? { ...a, status } : a);
      saveErpAppointments(updated);
      return updated;
    });

    if (userId) {
      try {
        await supabase.from('appointments').update({
          status,
          updated_at: new Date().toISOString()
        }).eq('id', id).eq('user_id', userId);
      } catch (err) {
        console.warn('Erro ao atualizar status do agendamento no Supabase:', err);
      }
    }
  };

  const deductPackageSession = async (id: string) => {
    let nextSession = 1;
    setAppointmentsInternal(prev => {
      const updated = prev.map(a => {
        if (a.id === id && a.packageCurrentSession && a.packageTotalSessions) {
          nextSession = a.packageCurrentSession + 1;
          return { ...a, packageCurrentSession: nextSession };
        }
        return a;
      });
      saveErpAppointments(updated);
      return updated;
    });

    if (userId) {
      try {
        await supabase.from('appointments').update({
          package_current_session: nextSession,
          updated_at: new Date().toISOString()
        }).eq('id', id).eq('user_id', userId);
      } catch (err) {
        console.warn('Erro ao atualizar sessão do pacote no Supabase:', err);
      }
    }
  };

  // Kanban / Gestão
  const [kanbanItems, setKanbanItemsInternal] = useState<PipelineItem[]>(() => loadErpKanban(DEFAULT_KANBAN_ITEMS));

  const moveKanbanStage = async (itemId: string, newStage: PipelineItem['stage']) => {
    setKanbanItemsInternal(prev => {
      const updated = prev.map(it => it.id === itemId ? { ...it, stage: newStage } : it);
      saveErpKanban(updated);
      return updated;
    });

    if (userId) {
      try {
        await supabase.from('quotes').update({
          status: newStage,
          updated_at: new Date().toISOString()
        }).eq('id', itemId).eq('user_id', userId);
      } catch (err) {
        console.warn('Erro ao atualizar status do orçamento no Supabase:', err);
      }
    }
  };

  const addKanbanQuote = (quote: { id: string; title: string; clientName: string; totalPrice: number; nicheKey: NicheKey }) => {
    setKanbanItemsInternal(prev => {
      const updated = [
        {
          id: quote.id,
          title: quote.title,
          clientName: quote.clientName,
          finalPrice: quote.totalPrice,
          nicheKey: quote.nicheKey,
          stage: 'draft' as const,
          updatedAt: 'Agora'
        },
        ...prev
      ];
      saveErpKanban(updated);
      return updated;
    });
  };

  // Perfil da Vitrine
  const [demoProfile, setDemoProfileInternal] = useState<StorefrontProfile>(() => loadErpProfile(DEFAULT_PROFILE));

  const setDemoProfile = (profile: StorefrontProfile) => {
    setDemoProfileInternal(profile);
    saveErpProfile(profile);
  };

  // Handler de Agendamento pelo Chatbot da Vitrine
  const handleBookFromChatbot = (appt: Appointment) => {
    saveAppointment(appt);

    // Auto-cadastro / atualização do cliente
    setClientsInternal(prev => {
      const existingIndex = prev.findIndex(c => c.name.toLowerCase() === appt.clientName.toLowerCase() || (appt.clientPhone && c.phone === appt.clientPhone));
      let updated: Client[];
      if (existingIndex >= 0) {
        updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          ordersCount: updated[existingIndex].ordersCount + 1,
          totalSpent: updated[existingIndex].totalSpent + appt.price,
          phone: appt.clientPhone || updated[existingIndex].phone,
          updatedAt: new Date().toISOString()
        };
      } else {
        const newClient: Client = {
          id: `cli_${Date.now()}`,
          userId: 'demo_user',
          name: appt.clientName,
          phone: appt.clientPhone,
          tags: ['Lead do Site', 'Novo'],
          totalSpent: appt.price,
          ordersCount: 1,
          source: 'chatbot_vitrine',
          notes: `Primeiro agendamento via Chatbot da Vitrine para ${appt.serviceName}.`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        updated = [newClient, ...prev];
      }
      saveErpClients(updated);
      return updated;
    });

    alert(`Agendamento realizado com sucesso para ${appt.clientName}! Horário reservado na agenda e cliente salvo na sua lista de clientes.`);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // MÓDULO ENTERPRISE: ESTOQUE, LOTES & MOVIMENTAÇÕES
  // ──────────────────────────────────────────────────────────────────────────
  const [inventoryBatches, setInventoryBatchesInternal] = useState<InventoryBatch[]>(() => 
    loadErpInventoryBatches([
      {
        id: 'batch_v10_01',
        userId: 'demo_user',
        materialId: 'mat_v10',
        materialName: 'Vacina V10 Polivalente Vanguard',
        batchCode: 'V10-2026-A',
        expirationDate: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        costPrice: 38.50,
        initialQuantity: 20,
        currentQuantity: 14,
        createdAt: new Date().toISOString()
      },
      {
        id: 'batch_ar_02',
        userId: 'demo_user',
        materialId: 'mat_ar',
        materialName: 'Vacina Antirrábica Defensor',
        batchCode: 'AR-8832',
        expirationDate: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        costPrice: 18.00,
        initialQuantity: 15,
        currentQuantity: 6,
        createdAt: new Date().toISOString()
      },
      {
        id: 'batch_mlx_03',
        userId: 'demo_user',
        materialId: 'mat_mlx',
        materialName: 'Meloxicam 0.5mg Gotas Frasco',
        batchCode: 'MLX-091',
        expirationDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        costPrice: 24.00,
        initialQuantity: 10,
        currentQuantity: 9,
        createdAt: new Date().toISOString()
      },
      {
        id: 'batch_srg_04',
        userId: 'demo_user',
        materialId: 'mat_srg',
        materialName: 'Seringa Descartável 3ml com Agulha',
        batchCode: 'SRG-2026',
        expirationDate: new Date(Date.now() + 360 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        costPrice: 0.90,
        initialQuantity: 200,
        currentQuantity: 142,
        createdAt: new Date().toISOString()
      }
    ])
  );

  const [stockMovements, setStockMovementsInternal] = useState<StockMovement[]>(() => 
    loadErpStockMovements([
      {
        id: 'mov_01',
        userId: 'demo_user',
        materialId: 'mat_v10',
        materialName: 'Vacina V10 Polivalente Vanguard',
        batchId: 'batch_v10_01',
        batchCode: 'V10-2026-A',
        type: 'entry_purchase',
        quantity: 20,
        unitCost: 38.50,
        totalCost: 770.00,
        balanceAfter: 20,
        reasonNotes: 'Entrada NF 19283 Fornecedor Zoetis',
        createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: 'mov_02',
        userId: 'demo_user',
        materialId: 'mat_v10',
        materialName: 'Vacina V10 Polivalente Vanguard',
        batchId: 'batch_v10_01',
        batchCode: 'V10-2026-A',
        type: 'exit_clinical_use',
        quantity: 1,
        unitCost: 38.50,
        totalCost: 38.50,
        balanceAfter: 19,
        reasonNotes: 'Aplicação em atendimento clínico',
        createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
      }
    ])
  );

  const saveInventoryBatch = async (batch: InventoryBatch) => {
    const isUUID = isValidUuid(batch.id);
    const finalBatchId = isUUID ? batch.id : crypto.randomUUID();
    let finalMaterialId = batch.materialId;

    if (userId && !isValidUuid(finalMaterialId)) {
      try {
        const matName = batch.materialName || 'Insumo de Estoque';
        const { data: existingMat } = await supabase
          .from('materials')
          .select('id')
          .eq('user_id', userId)
          .ilike('name', matName)
          .maybeSingle();

        if (existingMat?.id) {
          finalMaterialId = existingMat.id;
        } else {
          const newMatId = crypto.randomUUID();
          await supabase.from('materials').insert({
            id: newMatId,
            user_id: userId,
            name: matName,
            price: batch.costPrice,
            stockquantity: batch.initialQuantity
          });
          finalMaterialId = newMatId;
        }
      } catch (err) {
        console.warn('Erro ao verificar material vinculado ao lote:', err);
      }
    }

    const batchToSave: InventoryBatch = {
      ...batch,
      id: finalBatchId,
      materialId: finalMaterialId,
      userId: currentUserId,
      updatedAt: new Date().toISOString()
    };

    setInventoryBatchesInternal(prev => {
      const exists = prev.some(b => b.id === batchToSave.id);
      const updated = exists ? prev.map(b => b.id === batchToSave.id ? batchToSave : b) : [batchToSave, ...prev];
      saveErpInventoryBatches(updated);
      return updated;
    });

    if (activeWorkspaceId && isValidUuid(finalMaterialId)) {
      try {
        await supabase.from('inventory_batches').upsert({
          id: batchToSave.id,
          user_id: activeWorkspaceId,
          material_id: finalMaterialId,
          batch_code: batchToSave.batchCode,
          expiration_date: batchToSave.expirationDate,
          cost_price: batchToSave.costPrice,
          initial_quantity: batchToSave.initialQuantity,
          current_quantity: batchToSave.currentQuantity,
          updated_at: batchToSave.updatedAt
        });
      } catch (err) {
        console.warn('Erro ao salvar lote no Supabase:', err);
      }
    }
  };

  const deleteInventoryBatch = async (id: string) => {
    setInventoryBatchesInternal(prev => {
      const updated = prev.filter(b => b.id !== id);
      saveErpInventoryBatches(updated);
      return updated;
    });

    if (activeWorkspaceId && isValidUuid(id)) {
      try {
        await supabase.from('inventory_batches').delete().eq('id', id);
      } catch (err) {
        console.warn('Erro ao excluir lote no Supabase:', err);
      }
    }
  };

  const saveStockMovement = async (movement: StockMovement) => {
    const isUUID = isValidUuid(movement.id);
    const finalMovId = isUUID ? movement.id : crypto.randomUUID();
    let finalMaterialId = movement.materialId;

    if (activeWorkspaceId && !isValidUuid(finalMaterialId)) {
      try {
        const matName = movement.materialName || 'Insumo de Estoque';
        const { data: existingMat } = await supabase
          .from('materials')
          .select('id')
          .eq('user_id', activeWorkspaceId)
          .ilike('name', matName)
          .maybeSingle();

        if (existingMat?.id) {
          finalMaterialId = existingMat.id;
        } else {
          const newMatId = crypto.randomUUID();
          await supabase.from('materials').insert({
            id: newMatId,
            user_id: activeWorkspaceId,
            name: matName,
            price: movement.unitCost,
            stockquantity: movement.quantity
          });
          finalMaterialId = newMatId;
        }
      } catch (err) {
        console.warn('Erro ao verificar material da movimentação:', err);
      }
    }

    const movToSave: StockMovement = {
      ...movement,
      id: finalMovId,
      materialId: finalMaterialId,
      userId: currentUserId,
      createdAt: movement.createdAt || new Date().toISOString()
    };

    setStockMovementsInternal(prev => {
      const updated = [movToSave, ...prev];
      saveErpStockMovements(updated);
      return updated;
    });

    if (activeWorkspaceId && isValidUuid(finalMaterialId)) {
      try {
        await supabase.from('stock_movements').insert({
          id: movToSave.id,
          user_id: activeWorkspaceId,
          material_id: finalMaterialId,
          batch_id: isValidUuid(movToSave.batchId) ? movToSave.batchId : null,
          type: movToSave.type,
          quantity: movToSave.quantity,
          unit_cost: movToSave.unitCost,
          balance_after: movToSave.balanceAfter,
          reference_id: isValidUuid(movToSave.referenceId) ? movToSave.referenceId : null,
          reason_notes: movToSave.reasonNotes || null
        });
      } catch (err) {
        console.warn('Erro ao registrar movimentação no Supabase:', err);
      }
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // MÓDULO ENTERPRISE: PACIENTES, PRONTUÁRIOS, VACINAS & EXAMES
  // ──────────────────────────────────────────────────────────────────────────
  const [clinicalPatients, setClinicalPatientsInternal] = useState<ClinicalPatient[]>(() => 
    loadErpClinicalPatients([
      {
        id: 'pat_01',
        userId: 'demo_user',
        clientId: 'c1',
        clientName: 'Roberto Silveira',
        clientPhone: '11987654321',
        kind: 'pet',
        name: 'Thor',
        species: 'Canina',
        breed: 'Golden Retriever',
        gender: 'M',
        isNeutered: true,
        microchipNumber: '981098102938472',
        coatColor: 'Dourado claro',
        birthDate: '2022-04-10',
        bloodType: 'DEA 1.1',
        knownAllergies: 'Alergia a Dipirona',
        chronicConditions: 'Sensibilidade articular nos membros posteriores',
        emergencyContact: 'Dra. Camila - (11) 97777-8888',
        active: true,
        createdAt: new Date().toISOString()
      },
      {
        id: 'pat_02',
        userId: 'demo_user',
        clientId: 'c2',
        clientName: 'Mariana Costa',
        clientPhone: '11998877665',
        kind: 'pet',
        name: 'Luna',
        species: 'Felina',
        breed: 'Siamês',
        gender: 'F',
        isNeutered: true,
        coatColor: 'Seal Point',
        birthDate: '2023-01-15',
        knownAllergies: 'Nenhuma conhecida',
        chronicConditions: 'Nenhuma',
        active: true,
        createdAt: new Date().toISOString()
      }
    ])
  );

  const [clinicalConsultations, setClinicalConsultationsInternal] = useState<ClinicalConsultation[]>(() => 
    loadErpClinicalConsultations([
      {
        id: 'cons_01',
        userId: 'demo_user',
        patientId: 'pat_01',
        patientName: 'Thor',
        subjectiveAnamnesis: 'Tutor relata que o paciente apresentou leve claudicação no membro pélvico esquerdo após corrida no parque há 2 dias. Sem perda de apetite, sem episódios de vômito ou diarreia.',
        objectiveExam: {
          temperatureCelsius: 38.6,
          weightKg: 32.4,
          heartRateBpm: 94,
          respiratoryRateRpm: 24,
          hydrationLevel: 'Normal (<5%)',
          bodyConditionScore: '3/5 - Peso Ideal',
          mucousMembraneColor: 'Normocoradas (Rosa)',
          capillaryRefillTimeSec: 2
        },
        assessmentDiagnosis: 'Suspeita de entorse muscular leve em gastrocnêmio esquerdo. Sem crepitação articular.',
        planTreatment: 'Repouso físico por 5 dias. Compressas mornas 2x/dia. Prescrição de Meloxicam por 3 dias.',
        prescriptions: [
          {
            id: 'presc_01',
            medicationName: 'Meloxicam 0.5mg Gotas',
            dosage: '16 gotas (0.1mg/kg)',
            route: 'Oral',
            frequency: 'A cada 24 horas',
            durationDays: 3,
            instructions: 'Administrar logo após a refeição principal'
          }
        ],
        consumedMaterials: [
          {
            materialId: 'mat_srg',
            materialName: 'Seringa Descartável 3ml com Agulha',
            batchId: 'batch_srg_04',
            batchCode: 'SRG-2026',
            quantity: 1,
            unitCost: 0.90
          }
        ],
        consultationFee: 180.00,
        materialsFee: 15.00,
        totalAmount: 195.00,
        status: 'finalized',
        finalizedAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
        professionalSignatureHash: 'CFMV-SP-92819-A7F2D8E9102B4',
        createdAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString()
      }
    ])
  );

  const [clinicalVaccinations, setClinicalVaccinationsInternal] = useState<ClinicalVaccination[]>(() => 
    loadErpClinicalVaccinations([
      {
        id: 'vac_01',
        userId: 'demo_user',
        patientId: 'pat_01',
        patientName: 'Thor',
        tutorName: 'Roberto Silveira',
        tutorPhone: '11987654321',
        materialId: 'mat_v10',
        batchId: 'batch_v10_01',
        vaccineName: 'Vacina V10 Polivalente',
        appliedAt: new Date(Date.now() - 360 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        nextDueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        reminderCount: 0,
        status: 'scheduled',
        createdAt: new Date().toISOString()
      },
      {
        id: 'vac_02',
        userId: 'demo_user',
        patientId: 'pat_02',
        patientName: 'Luna',
        tutorName: 'Mariana Costa',
        tutorPhone: '11998877665',
        vaccineName: 'Vacina Quádrupla Felina V4',
        appliedAt: new Date(Date.now() - 370 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        nextDueDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        reminderCount: 1,
        lastRemindedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'overdue',
        createdAt: new Date().toISOString()
      },
      {
        id: 'vac_03',
        userId: 'demo_user',
        patientId: 'pat_01',
        patientName: 'Thor',
        tutorName: 'Roberto Silveira',
        tutorPhone: '11987654321',
        vaccineName: 'Vermifugação Drontal Plus',
        appliedAt: new Date(Date.now() - 85 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        nextDueDate: new Date(Date.now() + 18 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        reminderCount: 0,
        status: 'pending',
        createdAt: new Date().toISOString()
      }
    ])
  );

  const [clinicalExams, setClinicalExamsInternal] = useState<ClinicalExamVault[]>(() => 
    loadErpClinicalExams([
      {
        id: 'exam_01',
        userId: 'demo_user',
        patientId: 'pat_01',
        patientName: 'Thor',
        consultationId: 'cons_01',
        title: 'Radiografia Digital Membro Pélvico e Coluna',
        examType: 'raio_x',
        reportPdfUrl: '',
        previewImageUrl: '',
        rawDicomR2Path: 'patients/pat_01/exam_01.dcm',
        rawFileSizeBytes: 18450200,
        dicomMetadata: {
          patientName: 'Thor',
          modality: 'DX',
          studyDate: '2026-08-28',
          manufacturer: 'Canon Medical Systems',
          windowCenter: 2048,
          windowWidth: 4096,
          bodyPartExamined: 'HINDLIMB'
        },
        legalRetentionUntil: '2031-08-28',
        createdAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString()
      }
    ])
  );

  const saveClinicalPatient = async (patient: ClinicalPatient) => {
    const isUUID = isValidUuid(patient.id);
    const finalPatientId = isUUID ? patient.id : crypto.randomUUID();
    const patientToSave: ClinicalPatient = {
      ...patient,
      id: finalPatientId,
      userId: currentUserId,
      updatedAt: new Date().toISOString()
    };

    setClinicalPatientsInternal(prev => {
      const exists = prev.some(p => p.id === patientToSave.id);
      const updated = exists ? prev.map(p => p.id === patientToSave.id ? patientToSave : p) : [patientToSave, ...prev];
      saveErpClinicalPatients(updated);
      return updated;
    });

    if (activeWorkspaceId && isValidUuid(patientToSave.clientId)) {
      try {
        await supabase.from('clinical_patients').upsert({
          id: patientToSave.id,
          user_id: activeWorkspaceId,
          client_id: patientToSave.clientId,
          kind: patientToSave.kind || 'pet',
          name: patientToSave.name,
          species: patientToSave.species || null,
          breed: patientToSave.breed || null,
          gender: patientToSave.gender || null,
          is_neutered: Boolean(patientToSave.isNeutered),
          microchip_number: patientToSave.microchipNumber || null,
          coat_color: patientToSave.coatColor || null,
          birth_date: patientToSave.birthDate || null,
          blood_type: patientToSave.bloodType || null,
          known_allergies: patientToSave.knownAllergies || null,
          chronic_conditions: patientToSave.chronicConditions || null,
          emergency_contact: patientToSave.emergencyContact || null,
          active: patientToSave.active !== false,
          updated_at: patientToSave.updatedAt
        });
      } catch (err) {
        console.warn('Erro ao salvar paciente no Supabase:', err);
      }
    }
  };

  const deleteClinicalPatient = async (id: string) => {
    setClinicalPatientsInternal(prev => {
      const updated = prev.filter(p => p.id !== id);
      saveErpClinicalPatients(updated);
      return updated;
    });

    if (activeWorkspaceId && isValidUuid(id)) {
      try {
        await supabase.from('clinical_patients').delete().eq('id', id);
      } catch (err) {
        console.warn('Erro ao excluir paciente no Supabase:', err);
      }
    }
  };

  const saveClinicalConsultation = async (consultation: ClinicalConsultation) => {
    const isUUID = isValidUuid(consultation.id);
    const finalId = isUUID ? consultation.id : crypto.randomUUID();
    const consultationToSave: ClinicalConsultation = {
      ...consultation,
      id: finalId,
      userId: currentUserId,
      updatedAt: new Date().toISOString()
    };

    setClinicalConsultationsInternal(prev => {
      const exists = prev.some(c => c.id === consultationToSave.id);
      const updated = exists ? prev.map(c => c.id === consultationToSave.id ? consultationToSave : c) : [consultationToSave, ...prev];
      saveErpClinicalConsultations(updated);
      return updated;
    });

    if (activeWorkspaceId && isValidUuid(consultationToSave.patientId)) {
      try {
        await supabase.from('clinical_consultations').upsert({
          id: consultationToSave.id,
          user_id: activeWorkspaceId,
          patient_id: consultationToSave.patientId,
          appointment_id: isValidUuid(consultationToSave.appointmentId) ? consultationToSave.appointmentId : null,
          staff_member_id: isValidUuid(consultationToSave.staffMemberId) ? consultationToSave.staffMemberId : null,
          subjective_anamnesis: consultationToSave.subjectiveAnamnesis || '',
          objective_exam: consultationToSave.objectiveExam || {},
          assessment_diagnosis: consultationToSave.assessmentDiagnosis || null,
          plan_treatment: consultationToSave.planTreatment || '',
          prescriptions: consultationToSave.prescriptions || [],
          consumed_materials: consultationToSave.consumedMaterials || [],
          consultation_fee: consultationToSave.consultationFee || 0,
          materials_fee: consultationToSave.materialsFee || 0,
          status: consultationToSave.status || 'in_progress',
          finalized_at: consultationToSave.finalizedAt || null,
          professional_signature_hash: consultationToSave.professionalSignatureHash || null,
          updated_at: consultationToSave.updatedAt
        });
      } catch (err) {
        console.warn('Erro ao salvar prontuário no Supabase:', err);
      }
    }
  };

  const saveClinicalVaccination = async (vaccination: ClinicalVaccination) => {
    const isUUID = isValidUuid(vaccination.id);
    const finalId = isUUID ? vaccination.id : crypto.randomUUID();
    const vacToSave: ClinicalVaccination = {
      ...vaccination,
      id: finalId,
      userId: currentUserId
    };

    setClinicalVaccinationsInternal(prev => {
      const exists = prev.some(v => v.id === vacToSave.id);
      const updated = exists ? prev.map(v => v.id === vacToSave.id ? vacToSave : v) : [vacToSave, ...prev];
      saveErpClinicalVaccinations(updated);
      return updated;
    });

    if (activeWorkspaceId && isValidUuid(vacToSave.patientId)) {
      try {
        await supabase.from('clinical_vaccinations').upsert({
          id: vacToSave.id,
          user_id: activeWorkspaceId,
          patient_id: vacToSave.patientId,
          consultation_id: isValidUuid(vacToSave.consultationId) ? vacToSave.consultationId : null,
          material_id: isValidUuid(vacToSave.materialId) ? vacToSave.materialId : null,
          batch_id: isValidUuid(vacToSave.batchId) ? vacToSave.batchId : null,
          vaccine_name: vacToSave.vaccineName,
          applied_at: vacToSave.appliedAt,
          next_due_date: vacToSave.nextDueDate,
          reminder_count: vacToSave.reminderCount || 0,
          last_reminded_at: vacToSave.lastRemindedAt || null,
          status: vacToSave.status || 'pending'
        });
      } catch (err) {
        console.warn('Erro ao salvar vacina no Supabase:', err);
      }
    }
  };

  const deleteClinicalVaccination = async (id: string) => {
    setClinicalVaccinationsInternal(prev => {
      const updated = prev.filter(v => v.id !== id);
      saveErpClinicalVaccinations(updated);
      return updated;
    });

    if (activeWorkspaceId && isValidUuid(id)) {
      try {
        await supabase.from('clinical_vaccinations').delete().eq('id', id);
      } catch (err) {
        console.warn('Erro ao excluir vacina no Supabase:', err);
      }
    }
  };

  const saveClinicalExam = async (exam: ClinicalExamVault) => {
    const isUUID = isValidUuid(exam.id);
    const finalId = isUUID ? exam.id : crypto.randomUUID();
    const examToSave: ClinicalExamVault = {
      ...exam,
      id: finalId,
      userId: currentUserId
    };

    setClinicalExamsInternal(prev => {
      const exists = prev.some(e => e.id === examToSave.id);
      const updated = exists ? prev.map(e => e.id === examToSave.id ? examToSave : e) : [examToSave, ...prev];
      saveErpClinicalExams(updated);
      return updated;
    });

    if (activeWorkspaceId && isValidUuid(examToSave.patientId)) {
      try {
        await supabase.from('clinical_exams_vault').upsert({
          id: examToSave.id,
          user_id: activeWorkspaceId,
          patient_id: examToSave.patientId,
          consultation_id: isValidUuid(examToSave.consultationId) ? examToSave.consultationId : null,
          title: examToSave.title,
          exam_type: examToSave.examType,
          report_pdf_url: examToSave.reportPdfUrl || null,
          preview_image_url: examToSave.previewImageUrl || null,
          raw_dicom_r2_path: examToSave.rawDicomR2Path || null,
          raw_file_size_bytes: examToSave.rawFileSizeBytes || null,
          dicom_metadata: examToSave.dicomMetadata || {},
          legal_retention_until: examToSave.legalRetentionUntil || null
        });
      } catch (err) {
        console.warn('Erro ao salvar exame no Supabase:', err);
      }
    }
  };

  const deleteClinicalExam = async (id: string) => {
    setClinicalExamsInternal(prev => {
      const updated = prev.filter(e => e.id !== id);
      saveErpClinicalExams(updated);
      return updated;
    });

    if (activeWorkspaceId && isValidUuid(id)) {
      try {
        await supabase.from('clinical_exams_vault').delete().eq('id', id);
      } catch (err) {
        console.warn('Erro ao excluir exame no Supabase:', err);
      }
    }
  };

  // Checkout Atômico da Consulta com baixa FIFO de lotes e emissão de recibo
  const executeConsultationCheckout = async (
    consultationId: string,
    consumedItems: ConsumedMaterialItem[],
    paymentMethod: string,
    amountPaid: number
  ) => {
    let currentBatches = [...inventoryBatches];
    const newMovements: StockMovement[] = [];

    for (const item of consumedItems) {
      let remainingToDeduct = item.quantity;
      const itemBatches = currentBatches
        .filter(b => b.materialId === item.materialId && b.currentQuantity > 0)
        .sort((a, b) => new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime());

      for (const batch of itemBatches) {
        if (remainingToDeduct <= 0) break;
        const deductQty = Math.min(batch.currentQuantity, remainingToDeduct);
        const newQty = batch.currentQuantity - deductQty;

        currentBatches = currentBatches.map(b => b.id === batch.id ? { ...b, currentQuantity: newQty } : b);
        remainingToDeduct -= deductQty;

        newMovements.push({
          id: `mov_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
          userId: currentUserId,
          materialId: item.materialId,
          materialName: item.materialName,
          batchId: batch.id,
          batchCode: batch.batchCode,
          type: 'exit_clinical_use',
          quantity: deductQty,
          unitCost: batch.costPrice,
          totalCost: deductQty * batch.costPrice,
          balanceAfter: newQty,
          referenceId: consultationId,
          reasonNotes: 'Baixa automática no checkout da consulta',
          createdAt: new Date().toISOString()
        });
      }
    }

    setInventoryBatchesInternal(currentBatches);
    saveErpInventoryBatches(currentBatches);

    if (newMovements.length > 0) {
      const updatedMovements = [...newMovements, ...stockMovements];
      setStockMovementsInternal(updatedMovements);
      saveErpStockMovements(updatedMovements);
    }

    const finalizedTime = new Date().toISOString();
    const existingConsultation = clinicalConsultations.find(c => c.id === consultationId);
    const signatureHash = await generateCfmvSignatureHash({
      consultationId,
      patientId: existingConsultation?.patientId,
      crmv: existingConsultation?.veterinarianCrmv,
      finalizedAt: finalizedTime
    });

    setClinicalConsultationsInternal(prev => {
      const updated = prev.map(c => {
        if (c.id === consultationId) {
          return {
            ...c,
            status: 'finalized' as const,
            finalizedAt: finalizedTime,
            professionalSignatureHash: signatureHash,
            updatedAt: finalizedTime
          };
        }
        return c;
      });
      saveErpClinicalConsultations(updated);
      return updated;
    });

    const receiptNumber = generateReceiptNumber();
    return {
      success: true,
      receiptNumber,
      signatureHash
    };
  };

  const recordVaccineWhatsAppSent = (vaccineId: string) => {
    setClinicalVaccinationsInternal(prev => {
      const updated = prev.map(v => {
        if (v.id === vaccineId) {
          return {
            ...v,
            reminderCount: (v.reminderCount || 0) + 1,
            lastRemindedAt: new Date().toISOString()
          };
        }
        return v;
      });
      saveErpClinicalVaccinations(updated);
      return updated;
    });
  };

  const [accountantAccesses, setAccountantAccesses] = useState<AccountantAccess[]>([
    {
      id: 'acc_demo_1',
      workspaceOwnerId: currentUserId,
      accountantEmail: 'contabilidade@escritoriofiscal.com.br',
      accountantName: 'Roberto Alves, CRC',
      accountingFirm: 'Alves & Associados Contabilidade',
      crcNumber: 'CRC-SP 184920/O',
      status: 'active',
      invitedAt: new Date(Date.now() - 30 * 86400000).toISOString(),
      acceptedAt: new Date(Date.now() - 28 * 86400000).toISOString(),
      permissions: {
        canExportTax: true,
        canViewReceipts: true,
        canViewMaterials: true,
        canViewClinicalData: false
      }
    }
  ]);

  const inviteAccountant = async (data: { email: string; name?: string; firm?: string; crc?: string }) => {
    const newAcc: AccountantAccess = {
      id: `acc_${Date.now()}`,
      workspaceOwnerId: currentUserId,
      accountantEmail: data.email,
      accountantName: data.name,
      accountingFirm: data.firm,
      crcNumber: data.crc,
      status: 'pending',
      invitedAt: new Date().toISOString(),
      permissions: {
        canExportTax: true,
        canViewReceipts: true,
        canViewMaterials: true,
        canViewClinicalData: false
      }
    };
    setAccountantAccesses(prev => [newAcc, ...prev]);
    if (userId) {
      try {
        await supabase.from('accountant_access').upsert({
          id: newAcc.id,
          workspace_owner_id: userId,
          accountant_email: newAcc.accountantEmail,
          accountant_name: newAcc.accountantName || null,
          accounting_firm: newAcc.accountingFirm || null,
          crc_number: newAcc.crcNumber || null,
          status: 'pending',
          invited_at: newAcc.invitedAt
        }, { onConflict: 'workspace_owner_id,accountant_email' });
      } catch (err) {
        console.warn('Erro ao convidar contador no Supabase:', err);
      }
    }
  };

  const revokeAccountant = async (id: string) => {
    setAccountantAccesses(prev => prev.filter(a => a.id !== id));
    if (userId) {
      try {
        await supabase.from('accountant_access').delete().eq('id', id).eq('workspace_owner_id', userId);
      } catch (err) {
        console.warn('Erro ao revogar contador no Supabase:', err);
      }
    }
  };

  return {
    currentView,
    setCurrentView,
    selectedNiche,
    setSelectedNiche,
    simulatedPlanTier,
    setSimulatedPlanTier,
    canAccess,
    handleActionClick,
    isUpgradeModalOpen,
    setIsUpgradeModalOpen,
    upgradeRequiredTier,
    setUpgradeRequiredTier,
    upgradeFeatureName,
    setUpgradeFeatureName,
    upgradeFeatureDescription,
    setUpgradeFeatureDescription,
    financialState,
    setFinancialState,
    calculatedVhbRate,
    clients,
    saveClient,
    updateClientVehicle,
    suppliers,
    saveSupplier,
    deleteSupplier,
    appointments,
    saveAppointment,
    updateAppointmentStatus,
    deductPackageSession,
    kanbanItems,
    moveKanbanStage,
    addKanbanQuote,
    demoProfile,
    setDemoProfile,
    handleBookFromChatbot,
    inventoryBatches,
    stockMovements,
    saveInventoryBatch,
    deleteInventoryBatch,
    saveStockMovement,
    clinicalPatients,
    saveClinicalPatient,
    deleteClinicalPatient,
    clinicalConsultations,
    saveClinicalConsultation,
    clinicalVaccinations,
    saveClinicalVaccination,
    deleteClinicalVaccination,
    clinicalExams,
    saveClinicalExam,
    deleteClinicalExam,
    executeConsultationCheckout,
    recordVaccineWhatsAppSent,
    accountantAccesses,
    inviteAccountant,
    revokeAccountant
  };
}
